import React, { useState, useEffect, useRef } from 'react';

const Contact = () => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '' });
  const [errors, setErrors] = useState({});
  const [isVisible, setIsVisible] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '' });
  const [loading, setLoading] = useState(false);
  const canvasRef = useRef(null);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  // Particle background canvas (same as About/Services)
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animationId;
    let particles = [];

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      initParticles();
    };
    const initParticles = () => {
      particles = [];
      const particleCount = Math.min(100, Math.floor(window.innerWidth / 20));
      for (let i = 0; i < particleCount; i++) {
        particles.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          radius: Math.random() * 2 + 1,
          vx: (Math.random() - 0.5) * 0.3,
          vy: (Math.random() - 0.5) * 0.2,
          alpha: Math.random() * 0.5 + 0.2,
        });
      }
    };
    const draw = () => {
      if (!ctx) return;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach(p => {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255, 217, 102, ${p.alpha})`;
        ctx.fill();
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0) p.x = canvas.width;
        if (p.x > canvas.width) p.x = 0;
        if (p.y < 0) p.y = canvas.height;
        if (p.y > canvas.height) p.y = 0;
      });
      animationId = requestAnimationFrame(draw);
    };
    window.addEventListener('resize', resize);
    resize();
    draw();
    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(animationId);
    };
  }, []);

  const validate = () => {
    let temp = {};
    if (!formData.name) temp.name = 'Name is required';
    // Email is optional: only validate format if provided
    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      temp.email = 'Email is invalid';
    }
    if (!formData.phone) temp.phone = 'Phone number is required';
    if (!formData.message) temp.message = 'Message is required';
    setErrors(temp);
    return Object.keys(temp).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validate()) {
      setLoading(true);
      const formDataToSend = new FormData();
      formDataToSend.append('access_key', '0a1423a0-618b-4adc-91f7-11dbeb17c96d');
      formDataToSend.append('name', formData.name);
      if (formData.email) formDataToSend.append('email', formData.email);
      formDataToSend.append('phone', formData.phone);
      formDataToSend.append('message', formData.message);
      try {
        const response = await fetch('https://api.web3forms.com/submit', {
          method: 'POST',
          body: formDataToSend
        });
        const result = await response.json();
        if (result.success) {
          setToast({ show: true, message: 'Message sent successfully!' });
          setFormData({ name: '', email: '', phone: '', message: '' });
        } else {
          setToast({ show: true, message: 'Failed to send message. Please try again.' });
        }
      } catch (error) {
        setToast({ show: true, message: 'An error occurred. Please try again.' });
      } finally {
        setLoading(false);
        setTimeout(() => setToast({ show: false, message: '' }), 3000);
      }
    }
  };

  const deepBlue = '#0A2F44';
  const silver = '#F0F4F8';
  const gold = '#FFD966';

  const styles = {
    pageWrapper: {
      backgroundColor: deepBlue,
      minHeight: '100vh',
      position: 'relative',
      overflowX: 'hidden',
    },
    canvas: {
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      pointerEvents: 'none',
      zIndex: 0,
    },
    content: {
      position: 'relative',
      zIndex: 1,
    },
    container: {
      maxWidth: '900px',
      margin: '0 auto',
      padding: '4rem 1.5rem',
      opacity: isVisible ? 1 : 0,
      transition: 'opacity 0.6s ease',
    },
    header: {
      textAlign: 'center',
      marginBottom: '3rem',
    },
    handshake: {
      fontSize: '3.5rem',
      color: gold,
      marginBottom: '0.5rem',
      filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))',
    },
    title: {
      fontSize: '2.5rem',
      fontWeight: 'bold',
      color: '#FFFFFF',
      marginBottom: '0.5rem',
      textShadow: '0 0 8px rgba(240,244,248,0.3)',
    },
    subtitle: {
      fontSize: '1.125rem',
      color: silver,
      maxWidth: '600px',
      margin: '0 auto',
    },
    formCard: {
      backgroundColor: 'rgba(255,255,255,0.05)',
      backdropFilter: 'blur(8px)',
      borderRadius: '24px',
      boxShadow: '0 10px 30px -10px rgba(0,0,0,0.2)',
      padding: '2rem',
      marginBottom: '2rem',
      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
      border: '1px solid rgba(255,255,255,0.1)',
    },
    form: {
      display: 'flex',
      flexDirection: 'column',
      gap: '1.25rem',
    },
    inputGroup: {
      display: 'flex',
      flexDirection: 'column',
    },
    input: {
      padding: '0.875rem 1rem',
      fontSize: '1rem',
      border: '1px solid rgba(255,255,255,0.2)',
      borderRadius: '12px',
      transition: 'all 0.2s ease',
      outline: 'none',
      backgroundColor: 'rgba(0,0,0,0.3)',
      color: silver,
      backdropFilter: 'blur(4px)',
    },
    error: {
      color: '#ff6b6b',
      fontSize: '0.8rem',
      marginTop: '0.25rem',
      paddingLeft: '0.5rem',
    },
    button: {
      backgroundColor: gold,
      color: deepBlue,
      fontWeight: 'bold',
      padding: '0.875rem',
      border: 'none',
      borderRadius: '12px',
      cursor: 'pointer',
      fontSize: '1rem',
      transition: 'background-color 0.2s, transform 0.1s',
      marginTop: '0.5rem',
    },
    map: {
      marginTop: '1rem',
      borderRadius: '20px',
      overflow: 'hidden',
      boxShadow: '0 5px 15px rgba(0,0,0,0.2)',
      border: '1px solid rgba(255,255,255,0.1)',
    },
    footer: {
      marginTop: '3rem',
      textAlign: 'center',
      borderTop: '1px solid rgba(255,255,255,0.1)',
      paddingTop: '2rem',
    },
    contactLine: {
      color: silver,
      fontSize: '0.9rem',
    },
    contactLink: {
      color: gold,
      textDecoration: 'none',
      transition: 'color 0.2s',
      fontWeight: '500',
    },
  };

  const handleInputFocus = (e) => {
    e.target.style.borderColor = gold;
    e.target.style.boxShadow = `0 0 0 3px rgba(255,217,102,0.2)`;
    e.target.style.backgroundColor = 'rgba(0,0,0,0.4)';
  };
  const handleInputBlur = (e) => {
    e.target.style.borderColor = 'rgba(255,255,255,0.2)';
    e.target.style.boxShadow = 'none';
    e.target.style.backgroundColor = 'rgba(0,0,0,0.3)';
  };
  const handleInputHover = (e) => {
    e.target.style.borderColor = gold;
    e.target.style.backgroundColor = 'rgba(0,0,0,0.4)';
  };
  const handleInputLeave = (e) => {
    if (document.activeElement !== e.target) {
      e.target.style.borderColor = 'rgba(255,255,255,0.2)';
      e.target.style.backgroundColor = 'rgba(0,0,0,0.3)';
    }
  };
  const handleButtonHover = (e, isHover) => {
    e.target.style.backgroundColor = isHover ? '#FFFFFF' : gold;
    if (isHover) e.target.style.transform = 'scale(1.01)';
    else e.target.style.transform = 'scale(1)';
  };
  const handleContactLinkHover = (e, isHover) => {
    e.target.style.color = isHover ? '#FFFFFF' : gold;
  };

  // Updated map embed with the new address
  const mapEmbedUrl = "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3800.456!2d83.337065!3d17.723278!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3a39436e9e1f2b0f%3A0x8a3f2d9c1e4b6a7d!2s39-30-73-1%2F69%20R.M.B.R.T.O%20Office%2C%20Ekalavya%20Colony%2C%20Vuda%20Madhavadhara%2C%20Visakhapatnam%2C%20Andhra%20Pradesh%20530048!5e0!3m2!1sen!2sin!4v1712345678901!5m2!1sen!2sin";

  return (
    <div style={styles.pageWrapper}>
      <canvas ref={canvasRef} style={styles.canvas} />
      <div style={styles.content}>
        <div style={styles.container}>
          <div style={styles.header}>
            <div style={styles.handshake}>🤝</div>
            <h1 style={styles.title}>Varahi Septic Tank Services</h1>
            <div style={{ width: '80px', height: '2px', background: gold, margin: '1rem auto', borderRadius: '2px' }} />
            <p style={styles.subtitle}>
              We're here to help with all your septic needs. Fill out the form and we'll get back to you.
            </p>
          </div>

          <div style={styles.formCard}>
            <form onSubmit={handleSubmit} style={styles.form}>
              <div style={styles.inputGroup}>
                <input
                  type="text"
                  name="name"
                  placeholder="Your Name"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  style={styles.input}
                  onFocus={handleInputFocus}
                  onBlur={handleInputBlur}
                  onMouseEnter={handleInputHover}
                  onMouseLeave={handleInputLeave}
                />
                {errors.name && <div style={styles.error}>{errors.name}</div>}
              </div>
              <div style={styles.inputGroup}>
                <input
                  type="email"
                  name="email"
                  placeholder="Email Address (Optional)"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  style={styles.input}
                  onFocus={handleInputFocus}
                  onBlur={handleInputBlur}
                  onMouseEnter={handleInputHover}
                  onMouseLeave={handleInputLeave}
                />
                {errors.email && <div style={styles.error}>{errors.email}</div>}
              </div>
              <div style={styles.inputGroup}>
                <input
                  type="tel"
                  name="phone"
                  placeholder="Mobile Number"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  style={styles.input}
                  onFocus={handleInputFocus}
                  onBlur={handleInputBlur}
                  onMouseEnter={handleInputHover}
                  onMouseLeave={handleInputLeave}
                />
                {errors.phone && <div style={styles.error}>{errors.phone}</div>}
              </div>
              <div style={styles.inputGroup}>
                <textarea
                  name="message"
                  placeholder="Your Message"
                  rows="4"
                  value={formData.message}
                  onChange={(e) => setFormData({...formData, message: e.target.value})}
                  style={{...styles.input, minHeight: '100px', resize: 'vertical'}}
                  onFocus={handleInputFocus}
                  onBlur={handleInputBlur}
                  onMouseEnter={handleInputHover}
                  onMouseLeave={handleInputLeave}
                />
                {errors.message && <div style={styles.error}>{errors.message}</div>}
              </div>
              <button
                type="submit"
                style={styles.button}
                onMouseEnter={(e) => handleButtonHover(e, true)}
                onMouseLeave={(e) => handleButtonHover(e, false)}
                disabled={loading}
              >
                {loading ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          </div>

          <div style={styles.map}>
            <iframe
              title="Google Maps - Varahi Septic Tank Services Location"
              src={mapEmbedUrl}
              width="100%"
              height="300"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
            />
          </div>

          <div style={styles.footer}>
            <div style={styles.contactLine}>
              <a
                href="tel:+919948803051"
                style={styles.contactLink}
                onMouseEnter={(e) => handleContactLinkHover(e, true)}
                onMouseLeave={(e) => handleContactLinkHover(e, false)}
              >
                📞 +91 9948803051
              </a>
              {' | '}
              <a
                href="mailto:varahisepticservices@gmail.com"
                style={styles.contactLink}
                onMouseEnter={(e) => handleContactLinkHover(e, true)}
                onMouseLeave={(e) => handleContactLinkHover(e, false)}
              >
                ✉️ varahisepticservices@gmail.com
              </a>
            </div>
          </div>
        </div>
      </div>

      {toast.show && (
        <div className="toast" style={{
          position: 'fixed',
          bottom: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          background: gold,
          color: deepBlue,
          padding: '12px 24px',
          borderRadius: '50px',
          fontWeight: 'bold',
          zIndex: 1000,
          animation: 'fadeInUp 0.3s ease',
          boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
        }}>
          {toast.message}
        </div>
      )}
      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateX(-50%) translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateX(-50%) translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default Contact;